import { useEffect, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { ProjectCostBreakdownChartPointsDocument } from 'generated/resources/graphql'
import { Moment } from 'moment'
import moment from 'moment'

import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ChartInsight } from 'modules/project_management/components/charts/ChartInsight'
import { getCostBreakdownTransformedData } from 'modules/project_management/components/charts/EvolutionChart/BreakdownEvolutionChart/getCostBreakdownTransformedData'
import { ResponsiveBreakdownChart } from 'modules/project_management/components/charts/ResponsiveBreakdownChart'
import { overviewFrequencyOptionsEnum } from 'modules/project_management/lib/constants'
import { OverviewFilterOption } from 'modules/project_management/lib/overviewOptions'

interface CostBreakdownChartProps {
  projectId: string
  startDate?: Moment
  endDate?: Moment
  selectedFilter: OverviewFilterOption
  selectedFrequency: overviewFrequencyOptionsEnum
  currency: string
}

export const BreakdownEvolutionChart: React.FC<CostBreakdownChartProps> = ({
  projectId,
  startDate = moment().startOf('week'),
  endDate = moment().endOf('week'),
  selectedFilter,
  selectedFrequency,
  currency,
}) => {
  const [activeSeries, setActiveSeries] = useState<string[]>([
    'fixed_cost',
    'labor_cost',
    'spending_cost',
  ])

  const { data } = useQuery(ProjectCostBreakdownChartPointsDocument, {
    variables: {
      projectId: projectId,
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      slotType: selectedFrequency,
    },
  })

  const breakdownData =
    data?.projectManagement.projectCostBreakdownChartPointsConnection.edges[0]?.node

  const fixedCostPoints = breakdownData?.fixedCost || []

  const fixedData = getCostBreakdownTransformedData({
    data: fixedCostPoints,
    id: 'fixed_cost',
    label: i18n.t('project_management.graphs.breakdown.cost.legends.fixed_cost'),
    color: 'peach1000',
    selectedFrequency,
  })

  const laborCostPoints = breakdownData?.laborCost || []

  const laborData = getCostBreakdownTransformedData({
    data: laborCostPoints,
    id: 'labor_cost',
    label: i18n.t('project_management.graphs.breakdown.cost.legends.labor_cost'),
    color: 'olive1000',
    selectedFrequency,
  })

  const spendingCostPoints = breakdownData?.spendingCost || []

  const spendingData = getCostBreakdownTransformedData({
    data: spendingCostPoints,
    id: 'spending_cost',
    label: i18n.t('project_management.graphs.breakdown.cost.legends.spendings'),
    color: 'indigo1000',
    selectedFrequency,
  })

  const registeredData = [laborData, spendingData, fixedData]

  const onChangeBreakdownDataShown = (newActiveSeries: string[]) => {
    setActiveSeries(newActiveSeries)
  }

  useEffect(() => {
    track(trackingEvents.projectManagement.overview.seeBreakdownChart, {
      selectedFrequency,
      activeSeries,
    })
  }, [activeSeries, selectedFrequency])

  return (
    <Box gap="s12">
      <Box flexDirection="row" gap="s6" justifyContent="spaceBetween" paddingTop="s12">
        <ChartInsight
          id={'registered_cost'}
          title={`${laborData.label} ${selectedFilter.label.toLocaleLowerCase()}`}
          startingValue={activeSeries.includes('labor_cost') ? laborData.min : undefined}
          endingValue={activeSeries.includes('labor_cost') ? laborData.max : undefined}
          color={activeSeries.includes('labor_cost') ? laborData.color : 'grey400'}
          currency={currency}
        />
        <ChartInsight
          id={'registered_cost'}
          title={`${spendingData.label} ${selectedFilter.label.toLocaleLowerCase()}`}
          startingValue={activeSeries.includes('spending_cost') ? spendingData.min : undefined}
          endingValue={activeSeries.includes('spending_cost') ? spendingData.max : undefined}
          color={activeSeries.includes('spending_cost') ? spendingData.color : 'grey400'}
          currency={currency}
        />
        <ChartInsight
          id={'registered_cost'}
          title={fixedData.label}
          startingValue={activeSeries.includes('fixed_cost') ? 0 : undefined}
          endingValue={activeSeries.includes('fixed_cost') ? fixedData?.max : undefined}
          color={activeSeries.includes('fixed_cost') ? fixedData.color : 'grey400'}
          currency={currency}
        />
      </Box>
      <Text.Normal color={'grey800'} weight="medium" size="300">
        {selectedFilter.subtitle}
      </Text.Normal>
      <ResponsiveBreakdownChart
        breakdownData={registeredData}
        unit={currency}
        selectedFrequency={selectedFrequency}
        onChangeBreakdownDataShown={onChangeBreakdownDataShown}
      />
    </Box>
  )
}
